import { Search, Clear as ClearIcon } from "@mui/icons-material";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import PropTypes from "prop-types";
import debounce from "lodash/debounce";
import { useEffect, useRef, useState } from "react";

const SearchBar = ({ placeholder, value, onChange, delay = 300, InputProps = {} }) => {
  const inputRef = useRef();
  
  // Determine if component is controlled (value prop is provided)
  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState(value ?? "");
  const [showClear, setShowClear] = useState(!!value);
  
  // Create debounced function once and store in ref (only used for uncontrolled mode)
  const debouncedChangeRef = useRef(null);
  
  // Initialize and update debounced function when dependencies change
  // Only needed for uncontrolled mode
  useEffect(() => {
    if (!isControlled) {
      debouncedChangeRef.current = debounce((val) => {
        onChange(val);
      }, delay);
      
      return () => {
        if (debouncedChangeRef.current) {
          debouncedChangeRef.current.cancel();
        }
      };
    }
  }, [onChange, delay, isControlled]);

  // Sync internal state with external value prop (for controlled mode)
  // Cancel any pending debounced calls to prevent stale data from being sent after external updates
  useEffect(() => {
    if (isControlled) {
      // Cancel any pending debounced call when external value changes
      if (debouncedChangeRef.current) {
        debouncedChangeRef.current.cancel();
      }
      setInternalValue(value);
      setShowClear(!!value);
    }
  }, [value, isControlled]);

  const handleChange = (event) => {
    const newVal = event.target.value;
    setInternalValue(newVal);
    setShowClear(!!newVal);
    
    if (isControlled) {
      // Controlled mode: call onChange immediately - parent manages state
      onChange(newVal);
    } else if (debouncedChangeRef.current) {
      // Uncontrolled mode: debounce the callback
      debouncedChangeRef.current(newVal);
    }
  };

  const handleClear = () => {
    // Cancel any pending debounced calls to prevent stale data from firing after clear
    if (debouncedChangeRef.current) {
      debouncedChangeRef.current.cancel();
    }
    setInternalValue("");
    setShowClear(false);
    // Call onChange immediately since clearing is a deliberate user action
    onChange("");
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape" && showClear) {
      handleClear();
      event.preventDefault();
    }
  };

  return (
    <TextField
      placeholder={placeholder || "Search..."}
      sx={{ flexGrow: 1, minWidth: 200, width: { xs: "100%", sm: "100%" } }}
      value={internalValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      inputRef={inputRef}
      variant="outlined"
      InputProps={{
        startAdornment:
          InputProps.startAdornment !== undefined ? (
            InputProps.startAdornment
          ) : (
            <InputAdornment position="start">
              <IconButton
                tabIndex={-1}
                sx={{ p: 0 }}
                disableRipple
                onMouseDown={(e) => e.preventDefault()}
              >
                <Search />
              </IconButton>
            </InputAdornment>
          ),
        endAdornment: showClear && (
          <InputAdornment position="end">
            <IconButton
              aria-label="clear text"
              onClick={handleClear}
              edge="end"
            >
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        ),
        ...InputProps,
      }}
    />
  );
};

SearchBar.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  delay: PropTypes.number,
  InputProps: PropTypes.object,
};

export default SearchBar;
